// src/pages/AuthCallback.tsx
//
// Handles email_change and signup confirmations.
// Recovery flow goes directly to /reset-password — NOT through here.
// But if somehow a recovery link lands here, we redirect to /reset-password.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

export const AuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');

    useEffect(() => {
        const handleCallback = async () => {
            console.log('[AuthCallback] hash:', window.location.hash);
            console.log('[AuthCallback] search:', window.location.search);

            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const queryParams = new URLSearchParams(window.location.search);

            const getParam = (key: string) =>
                hashParams.get(key) ?? queryParams.get(key) ?? null;

            const error            = getParam('error');
            const errorCode        = getParam('error_code');
            const errorDescription = getParam('error_description');
            const type             = getParam('type');

            console.log('[AuthCallback] parsed:', { type, error, errorCode });

            try {
                // ── Error ─────────────────────────────────────────────────────
                if (error) {
                    const isExpired =
                        errorCode === 'otp_expired' ||
                        (errorDescription ?? '').toLowerCase().includes('expired') ||
                        (errorDescription ?? '').toLowerCase().includes('invalid');

                    setStatus('error');
                    setMessage(
                        isExpired
                            ? 'Ссылка устарела или уже была использована. Запросите новую.'
                            : errorDescription || 'Ошибка подтверждения'
                    );
                    return;
                }

                // ── Recovery — redirect to ResetPassword ──────────────────────
                // Should not normally come here (redirectTo points to /reset-password),
                // but handle it just in case.
                if (type === 'recovery') {
                    navigate('/reset-password', { replace: true });
                    return;
                }

                // ── Email change ──────────────────────────────────────────────
                if (type === 'email_change') {
                    setStatus('success');
                    setMessage('Email успешно изменён!');
                    toast.success('Email успешно изменён!');
                    setTimeout(() => navigate('/profile', { replace: true }), 1500);
                    return;
                }

                // ── Signup / invite ───────────────────────────────────────────
                if (type === 'signup' || type === 'magiclink' || type === 'invite') {
                    const { data: { session } } = await supabase.auth.getSession();
                    setStatus('success');
                    setMessage('Email успешно подтверждён!');
                    toast.success('Email подтверждён!');
                    setTimeout(() => navigate(session ? '/' : '/login', { replace: true }), 1500);
                    return;
                }

                // ── Unknown ───────────────────────────────────────────────────
                const { data: { session } } = await supabase.auth.getSession();
                navigate(session ? '/' : '/login', { replace: true });

            } catch (err) {
                console.error('[AuthCallback] error:', err);
                setStatus('error');
                setMessage('Произошла ошибка при обработке запроса');
            }
        };

        handleCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="auth-callback-container">
            <div className="auth-callback-card">
                {status === 'loading' && (
                    <>
                        <div className="spinner"></div>
                        <h2>Обработка запроса...</h2>
                        <p>Пожалуйста, подождите</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <div className="success-icon">✅</div>
                        <h2>{message}</h2>
                        <p>Перенаправляем вас...</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <div className="error-icon">❌</div>
                        <h2>Ошибка</h2>
                        <p>{message}</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="auth-button"
                            style={{ marginTop: '20px' }}
                        >
                            Перейти на страницу входа
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
